import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { SocialAuthButtons } from "../components/SocialAuthButtons";
import CountryPicker from "../components/CountryPicker";

const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";
const USERNAME_RE = /^[a-zA-Z0-9_]{3,20}$/;

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryNext = new URLSearchParams(location.search).get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const usernameInvalid = username && !USERNAME_RE.test(username);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    if (password.length < 8) { setErr("Password must be at least 8 characters."); setBusy(false); return; }
    if (!USERNAME_RE.test(username)) { setErr("Username must be 3–20 chars; letters, numbers, underscore."); setBusy(false); return; }
    if (!firstName.trim() || !lastName.trim()) { setErr("First and last name are required."); setBusy(false); return; }
    if (!country) { setErr("Country is required."); setBusy(false); return; }
    try {
      await register({ email, password, username, first_name: firstName, last_name: lastName, country });
      navigate(queryNext || "/dashboard", { replace: true });
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : detail?.[0]?.msg || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    if (queryNext) sessionStorage.setItem("postAuthNext", queryNext);
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div data-testid="register-page" className="min-h-screen bg-[var(--bg)] text-zinc-100 flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="text-xs uppercase tracking-[0.4em] text-[var(--accent)] inline-block mb-5">← back</Link>
        <div className="flex items-center justify-center mb-4">
          <Logo to="/" size={44} />
        </div>
        <h1 className="text-4xl font-bold text-center">Join FootGuesser.</h1>
        <p className="text-sm text-zinc-400 mt-2 text-center">Pick a permanent handle and you&apos;re in.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">Username</label>
            <input
              data-testid="register-username"
              type="text" required minLength={3} maxLength={20}
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/\s+/g, ""))}
              placeholder=""
              className={`mt-1 w-full px-4 py-3 rounded-lg bg-zinc-900 border outline-none transition ${usernameInvalid ? "border-red-500" : "border-white/10 focus:border-[var(--accent)]"}`}
            />
            <div className="text-[11px] text-zinc-500 mt-1">3–20 chars · letters, numbers, underscore · <span className="text-amber-300">cannot be changed later</span></div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">Email</label>
            <input
              data-testid="register-email"
              type="email" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full px-4 py-3 rounded-lg bg-zinc-900 border border-white/10 focus:border-[var(--accent)] outline-none transition"
            />
            <div className="text-[11px] text-zinc-500 mt-1"><span className="text-amber-300">cannot be changed later</span></div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">Password</label>
            <input
              data-testid="register-password"
              type="password" required minLength={8} autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full px-4 py-3 rounded-lg bg-zinc-900 border border-white/10 focus:border-[var(--accent)] outline-none transition"
            />
            <div className="text-[11px] text-zinc-500 mt-1">8+ characters.</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">First name</label>
              <input
                data-testid="register-first-name"
                type="text" required maxLength={50}
                value={firstName} onChange={(e) => setFirstName(e.target.value)}
                className="mt-1 w-full px-4 py-3 rounded-lg bg-zinc-900 border border-white/10 focus:border-[var(--accent)] outline-none transition"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Last name</label>
              <input
                data-testid="register-last-name"
                type="text" required maxLength={50}
                value={lastName} onChange={(e) => setLastName(e.target.value)}
                className="mt-1 w-full px-4 py-3 rounded-lg bg-zinc-900 border border-white/10 focus:border-[var(--accent)] outline-none transition"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">Country</label>
            <CountryPicker
              value={country}
              onChange={setCountry}
              required
              testId="register-country"
            />
          </div>

          {err && (
            <div data-testid="register-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {err}
            </div>
          )}
          <button type="submit" disabled={busy}
            data-testid="register-submit"
            className="w-full px-5 py-3 rounded-full bg-[var(--accent)] text-black font-bold hover:brightness-110 disabled:opacity-50 transition">
            {busy ? "creating…" : "Create account"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-6 text-xs text-zinc-500">
          <div className="flex-1 h-px bg-white/10" /> OR <div className="flex-1 h-px bg-white/10" />
        </div>

        <SocialAuthButtons onGoogle={googleLogin} testIdPrefix="register" />

        <p className="text-sm text-zinc-400 mt-6 text-center">
          Already have an account?{" "}
          <Link
            to={queryNext ? `/login?next=${encodeURIComponent(queryNext)}` : "/login"}
            data-testid="link-to-login"
            className="text-[var(--accent)] hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
